import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Mail, MapPin, Phone } from 'lucide-react';
import { COURRIEL_VIE_PRIVEE, DOCUMENT_SOURCE } from '../../lib/confidentialite';
import { prochainesActivites } from '../../lib/seo/prochainesActivites';
import type { Activite } from '../../lib/seo/catalogue';

const Footer = () => {
  /*
    Vide tant que la lecture n'a pas abouti, et vide si elle échoue : le pied
    de page garde alors son lien vers le catalogue, qui suffit. Un pied de page
    n'a pas à annoncer une panne réseau.
  */
  const [activites, setActivites] = useState<Activite[]>([]);

  useEffect(() => {
    let vivant = true;
    prochainesActivites()
      .then((a) => {
        if (vivant) setActivites(a);
      })
      .catch(() => {
        /* silence volontaire : voir plus haut. */
      });
    return () => {
      vivant = false;
    };
  }, []);

  return (
    <footer className="bg-gray-100 pt-12 pb-8 mt-12">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-1">
            {/*
              « KidsCamp » a survecu ici a tout le reste du nettoyage : c'est
              une marque qui n'est ni celle du site ni celle de l'association,
              et elle etait servie sur chaque page.
            */}
            <div className="flex items-center gap-2 mb-4">
              <img
                src="/icone-eclosion.png"
                alt=""
                className="h-8 w-8"
                width={32}
                height={32}
              />
              <span className="flex flex-col leading-tight">
                <span className="text-xl font-bold text-primary-800">Co-nest</span>
                <span className="text-[11px] text-gray-500">Éclosion ASBL</span>
              </span>
            </div>
            <p className="text-gray-600 mb-4">
              Éclosion ASBL organise des plaines de vacances et des stages à thème pour
              enfants de 2,5 à 13 ans, à Bruxelles.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/profile.php?id=100064784646762" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 hover:text-primary-600"
              >
                <span className="sr-only">Facebook</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/eclosionasbl?igsh=cWpoOGZvY2JranM4" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 hover:text-primary-600"
              >
                <span className="sr-only">Instagram</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Navigation</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-600 hover:text-primary-600">
                  Accueil
                </Link>
              </li>
              <li>
                <Link to="/activities" className="text-gray-600 hover:text-primary-600">
                  Stages
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-gray-600 hover:text-primary-600">
                  Se connecter
                </Link>
              </li>
              <li>
                <Link to="/register" className="text-gray-600 hover:text-primary-600">
                  S'inscrire
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Nos prochains stages</h3>
            {/*
              Les vraies activités à venir, et non les cinq catégories de la
              maquette — Nature, Cirque, Science, Multisport, Cuisine — qui
              pointaient vers `/activities?category=…` : une catégorie qui
              n'existe dans aucune table, derrière une page de connexion. Un
              visiteur non connecté y trouvait un mur de login, un assistant
              rien du tout.
            */}
            <ul className="space-y-2">
              {activites.map((a) => (
                <li key={a.nom}>
                  <Link
                    to={`/stages/${a.vitrine.slug}`}
                    className="text-gray-600 hover:text-primary-600"
                  >
                    {a.nom}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/stages" className="text-primary-700 hover:text-primary-900">
                  {activites.length > 0 ? 'Voir tout le catalogue' : 'Voir nos stages'}
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <MapPin size={18} className="text-primary-600 mr-2 mt-0.5" />
                <span className="text-gray-600">Rue des Palais 32-34, 1030 Schaerbeek</span>
              </li>
              <li className="flex items-center">
                <Phone size={18} className="text-primary-600 mr-2" />
                <span className="text-gray-600">+32 470 470 503</span>
              </li>
              <li className="flex items-center">
                <Mail size={18} className="text-primary-600 mr-2" />
                <span className="text-gray-600">info@eclosion.be</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
            <p className="text-gray-500 text-sm order-2 sm:order-1">
              &copy; {new Date().getFullYear()} Éclosion ASBL. Tous droits réservés.
            </p>
            {/*
              Le lien vers la politique de confidentialité vit ici, sur TOUTES
              les pages : c'est de là qu'on l'attend, et une famille qui la
              cherche ne sait pas dans quel écran elle a été affichée.
            */}
            <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm order-1 sm:order-2">
              <li>
                <Link to="/confidentialite" className="text-gray-600 hover:text-primary-600">
                  Politique de confidentialité
                </Link>
              </li>
              <li>
                <a
                  href={DOCUMENT_SOURCE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-gray-500 hover:text-primary-600"
                >
                  Document source
                  <ExternalLink className="ml-1 h-3 w-3" />
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${COURRIEL_VIE_PRIVEE}`}
                  className="text-gray-600 hover:text-primary-600"
                >
                  Vie privée
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;